import { useState, useEffect, useRef, useCallback } from 'react';
import React from 'react';
import {
    Dialog,
    Box,
    TextField,
    IconButton,
    Typography,
    Paper,
    Avatar,
} from '@mui/material';
import {
    Send as SendIcon,
    MoreVert as MoreVertIcon,
    ArrowBack as ArrowBackIcon,
    DoneAll as DoneAllIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Node, Edge } from 'reactflow';

// Função para renderizar texto formatado (negrito e itálico)
const renderFormattedText = (text: string): React.ReactNode => {
    if (!text) return null;
    
    const processFormattedText = (content: string, key: number = 0): React.ReactNode => {
        const boldRegex = /\*([^*]+)\*/;
        const italicRegex = /_([^_]+)_/;
        
        const boldMatch = content.match(boldRegex);
        const italicMatch = content.match(italicRegex);
        
        let firstMatch: { type: 'bold' | 'italic'; match: RegExpMatchArray; index: number } | null = null;
        
        if (boldMatch && (!italicMatch || (boldMatch.index! < italicMatch.index!))) {
            firstMatch = { type: 'bold', match: boldMatch, index: boldMatch.index! };
        } else if (italicMatch) {
            firstMatch = { type: 'italic', match: italicMatch, index: italicMatch.index! };
        }
        
        if (firstMatch) {
            const beforeFormat = content.substring(0, firstMatch.index);
            const formatContent = firstMatch.match[1];
            const afterFormat = content.substring(firstMatch.index + firstMatch.match[0].length);
            
            const parts: React.ReactNode[] = [];
            
            if (beforeFormat) {
                parts.push(processFormattedText(beforeFormat, key * 10));
            }
            
            const nestedParts = processFormattedText(formatContent, key * 10 + 1);
            
            if (firstMatch.type === 'bold') {
                parts.push(<strong key={`bold-${key}`}>{nestedParts}</strong>);
            } else {
                parts.push(<em key={`italic-${key}`}>{nestedParts}</em>);
            }
            
            if (afterFormat) {
                parts.push(processFormattedText(afterFormat, key * 10 + 2));
            }
            
            return <>{parts}</>;
        }
        
        return <React.Fragment key={`text-${key}`}>{content}</React.Fragment>;
    };
    
    const lines = text.split('\n');
    return (
        <>
            {lines.map((line, index) => (
                <React.Fragment key={index}>
                    {processFormattedText(line, index)}
                    {index < lines.length - 1 && <br />}
                </React.Fragment>
            ))}
        </>
    );
};

interface Message {
    id: string;
    text: string;
    sender: 'bot' | 'user';
    timestamp: string;
}

interface FlowSimulatorProps {
    open: boolean;
    onClose: () => void;
    nodes: Node[];
    edges: Edge[];
}

export const FlowSimulator = ({ open, onClose, nodes, edges }: FlowSimulatorProps) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState('');
    const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
    const [isTyping, setIsTyping] = useState(false);
    const [variables, setVariables] = useState<Record<string, any>>({
        nome: 'João Silva',
        telefone: '5511999999999',
        ticketId: '12345',
        cliente_id: null as number | null,
    });
    const [faturasSimState, setFaturasSimState] = useState<{
        phase: string;
        clienteId?: number;
        cpfAttempts?: number;
        selectedContratoId?: number | null;
        contratoOptions?: { contratoId: number | null; endereco: string }[];
    }>({ phase: 'init' });

    const MOCK_FATURAS = [
        { id: 101, mes_referencia: '2026-06', data_vencimento: '15/06/2026', valor: '99,90', descricao: 'Mensalidade', contrato_id: 501, endereco: 'Rua Alpha, 100' },
        { id: 102, mes_referencia: '2026-05', data_vencimento: '15/05/2026', valor: '99,90', descricao: 'Mensalidade', contrato_id: 501, endereco: 'Rua Alpha, 100' },
        { id: 103, mes_referencia: '2026-04', data_vencimento: '15/04/2026', valor: '89,90', descricao: 'Mensalidade', contrato_id: 502, endereco: 'Av Beta, 200' },
    ];

    const sendBotMessage = (text: string, delay = 1000) => {
        setIsTyping(true);
        setTimeout(() => {
            setMessages(prev => [...prev, {
                id: Math.random().toString(36).substr(2, 9),
                text,
                sender: 'bot',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            }]);
            setIsTyping(false);
        }, delay);
    };

    const formatMockFaturasList = (quantidade: number, contratoId?: number | null) => {
        const emojis = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
        return MOCK_FATURAS
            .filter(f => contratoId === undefined || f.contrato_id === contratoId)
            .slice(0, quantidade)
            .map((f, i) =>
                `${emojis[i] || `${i + 1}.`} - #${f.id} - ${f.mes_referencia} - ${f.data_vencimento} - R$ ${f.valor}`
            ).join('\n');
    };

    const sendMockFaturaDirect = (fatura: (typeof MOCK_FATURAS)[0], nodeId: string, data: any) => {
        if (data.sendMode === 'link') {
            sendBotMessage(`[Template enviar_fatura] Fatura #${fatura.id} - R$ ${fatura.valor}`, 500);
        } else {
            sendBotMessage(`Segue dados da sua fatura\nFatura: #${fatura.id}\nValor: R$ ${fatura.valor}\nVencimento: ${fatura.data_vencimento}`, 500);
            setTimeout(() => sendBotMessage('Linha digitavel: 12345...\nPIX: pix...', 500), 1200);
            setTimeout(() => sendBotMessage(data.mensagem4 || 'Enviei os dados da fatura para pagamento.', 500), 2400);
        }
        const branch = edges.find(e => e.source === nodeId && e.sourceHandle === 'success');
        if (branch) setTimeout(() => processNode(branch.target), 3500);
    };

    const showFaturasOrSendDirect = (nodeId: string, data: any, quantidade: number, clienteId: number, contratoId?: number | null) => {
        const faturas = MOCK_FATURAS
            .filter(f => contratoId === undefined || f.contrato_id === contratoId)
            .slice(0, quantidade);
        if (faturas.length === 1) {
            sendMockFaturaDirect(faturas[0]!, nodeId, data);
            setFaturasSimState({ phase: 'done', clienteId, selectedContratoId: contratoId ?? null });
            return;
        }
        sendBotMessage(`${replaceVariables(data.listIntroMessage || 'Suas faturas em aberto:')}\n${formatMockFaturasList(faturas.length, contratoId)}`);
        setFaturasSimState({ phase: 'choose', clienteId, selectedContratoId: contratoId ?? null });
    };

    const resolveContratoOrListFaturasV2Sim = (nodeId: string, data: any, clienteId: number) => {
        const quantidade = data.quantidade || 3;
        const faturas = MOCK_FATURAS.slice(0, quantidade);
        const contratoMap = new Map<number, typeof MOCK_FATURAS>();
        for (const f of faturas) {
            const list = contratoMap.get(f.contrato_id) || [];
            list.push(f);
            contratoMap.set(f.contrato_id, list);
        }
        const options = Array.from(contratoMap.entries()).map(([contratoId, group]) => ({
            contratoId,
            endereco: group[0]?.endereco || 'Endereco nao informado',
        }));

        if (options.length <= 1) {
            showFaturasOrSendDirect(nodeId, data, quantidade, clienteId, options[0]?.contratoId);
            return;
        }

        const emojis = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣'];
        const intro = (data.chooseContractMessage || 'Localizei {{quantidade_contratos}} Contratos, qual deseja consultar:')
            .replace('{{quantidade_contratos}}', String(options.length));
        const list = options.map((opt, i) => `${emojis[i] || `${i + 1}.`} - #${opt.contratoId} - ${opt.endereco}`).join('\n');
        sendBotMessage(`${intro}\n${list}`);
        setFaturasSimState({ phase: 'chooseContract', clienteId, contratoOptions: options });
    };

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (open) {
            resetSimulator();
        } else {
            setMessages([]);
            setCurrentNodeId(null);
            setIsTyping(false);
        }
    }, [open]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const resetSimulator = () => {
        setMessages([]);
        setIsTyping(false);
        setFaturasSimState({ phase: 'init' });
        setVariables({
            nome: 'João Silva',
            telefone: '5511999999999',
            ticketId: '12345',
            cliente_id: null,
        });
        const startNode = nodes.find(n => n.id === 'start');
        if (startNode) {
            processNode(startNode.id);
        }
    };

    const processNode = useCallback((nodeId: string) => {
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return;

        setCurrentNodeId(nodeId);
        // Teste

        // Lógica de processamento
        switch (node.data.type || node.type) {
            case 'message': {
                const text = replaceVariables(node.data.text || node.data.label || '');
                if (text) {
                    setIsTyping(true);
                    setTimeout(() => {
                        const newMsg: Message = {
                            id: Math.random().toString(36).substr(2, 9),
                            text,
                            sender: 'bot',
                            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        };
                        setMessages(prev => [...prev, newMsg]);
                        setIsTyping(false);

                        const conn = edges.find(e => e.source === nodeId);
                        if (conn) {
                            setTimeout(() => processNode(conn.target), 1000);
                        }
                    }, 1500);
                } else {
                    const conn = edges.find(e => e.source === nodeId);
                    if (conn) processNode(conn.target);
                }
                break;
            }

            case 'menu': {
                let menuText = replaceVariables(node.data.message || 'Escolha uma opção:');
                const options = node.data.options || [];
                const emojis = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
                options.forEach((opt: any, index: number) => {
                    const emoji = emojis[index] || `${index + 1}.`;
                    menuText += `\n${emoji} - ${opt.label}`;
                });

                setIsTyping(true);
                setTimeout(() => {
                    const newMsg: Message = {
                        id: Math.random().toString(36).substr(2, 9),
                        text: menuText,
                        sender: 'bot',
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    };
                    setMessages(prev => [...prev, newMsg]);
                    setIsTyping(false);
                }, 1000);
                break;
            }

            case 'condition': {
                const key = node.data.key;
                const condition = node.data.condition;
                const value = node.data.value;
                const varValue = variables[key];

                let result = false;
                if (condition === 'equals') result = String(varValue) === String(value);
                else if (condition === 'contains') result = String(varValue).includes(String(value));
                else if (condition === 'not_equals') result = String(varValue) !== String(value);

                const branch = edges.find(e => e.source === nodeId && e.sourceHandle === (result ? 'true' : 'false'));
                if (branch) {
                    setTimeout(() => processNode(branch.target), 600);
                }
                break;
            }

            case 'identifyClient': {
                const hasClient = Boolean(variables.cliente_id);
                const branch = edges.find(e => e.source === nodeId && e.sourceHandle === (hasClient ? 'true' : 'false'));
                if (branch) {
                    setTimeout(() => processNode(branch.target), 600);
                }
                break;
            }

            case 'consultarFaturas': {
                const data = node.data || {};
                if (variables.cliente_id) {
                    showFaturasOrSendDirect(nodeId, data, data.quantidade || 3, variables.cliente_id);
                } else {
                    sendBotMessage(replaceVariables(data.askCpfMessage || 'Para consultar suas faturas, informe seu CPF ou CNPJ:'));
                    setFaturasSimState({ phase: 'askCpf', cpfAttempts: 0 });
                }
                break;
            }

            case 'consultarFaturasV2': {
                const data = node.data || {};
                if (variables.cliente_id) {
                    resolveContratoOrListFaturasV2Sim(nodeId, data, variables.cliente_id);
                } else {
                    sendBotMessage(replaceVariables(data.askCpfMessage || 'Para consultar suas faturas, informe seu CPF ou CNPJ:'));
                    setFaturasSimState({ phase: 'askCpf', cpfAttempts: 0 });
                }
                break;
            }

            case 'businessHours': {
                // Simulador: usa horário mock (08:00-18:00, seg-sex) - em produção usa config real
                const DAY_MAP: Record<number, string> = { 0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'saturday' };
                const now = new Date();
                const day = DAY_MAP[now.getDay()];
                const currentTime = now.toTimeString().slice(0, 5);
                const mockSchedule: Record<string, { enabled: boolean; start: string; end: string }> = {
                    monday: { enabled: true, start: '08:00', end: '18:00' }, tuesday: { enabled: true, start: '08:00', end: '18:00' },
                    wednesday: { enabled: true, start: '08:00', end: '18:00' }, thursday: { enabled: true, start: '08:00', end: '18:00' },
                    friday: { enabled: true, start: '08:00', end: '18:00' }, saturday: { enabled: false, start: '08:00', end: '12:00' },
                    sunday: { enabled: false, start: '08:00', end: '12:00' },
                };
                const schedule = mockSchedule[day];
                const isOutOfHours = !schedule?.enabled || currentTime < (schedule?.start || '') || currentTime > (schedule?.end || '');

                if (isOutOfHours && node.data?.sendMessageOutOfHours && node.data?.outOfHoursMessage) {
                    const text = replaceVariables(node.data.outOfHoursMessage);
                    const newMsg: Message = {
                        id: Math.random().toString(36).substr(2, 9),
                        text,
                        sender: 'bot',
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    };
                    setMessages(prev => [...prev, newMsg]);
                }

                const handleOut = isOutOfHours ? 'outside' : 'inside';
                const branch = edges.find(e => e.source === nodeId && e.sourceHandle === handleOut);
                if (branch) {
                    setTimeout(() => processNode(branch.target), 600);
                }
                break;
            }

            case 'closedTicketByBot': {
                setIsTyping(true);
                setTimeout(() => {
                    const newMsg: Message = {
                        id: Math.random().toString(36).substr(2, 9),
                        text: '🏁 _Atendimento encerrado pelo bot.',
                        sender: 'bot',
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    };
                    setMessages(prev => [...prev, newMsg]);
                    setIsTyping(false);
                }, 1000);
                break;
            } 

            case 'stopBot': {
                const text = node.data?.text;
                setIsTyping(true);
                setTimeout(() => {
                    const newMsg: Message = {
                        id: Math.random().toString(36).substr(2, 9),
                        text: `🚫 _Bot Desativado_\n\n${text}`,
                        sender: 'bot',
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    };
                    setMessages(prev => [...prev, newMsg]);
                    setIsTyping(false);
                }, 1000);
                break;
            }

            case 'question': {
                const text = replaceVariables(node.data.text || 'Digite sua resposta:');
                setIsTyping(true);
                setTimeout(() => {
                    const newMsg: Message = {
                        id: Math.random().toString(36).substr(2, 9),
                        text,
                        sender: 'bot',
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    };
                    setMessages(prev => [...prev, newMsg]);
                    setIsTyping(false);
                }, 1000);
                break;
            }

            case 'changeQueue': {
                const queueName = node.data?.queueName || 'uma nova fila';
                const newMsg: Message = {
                    id: Math.random().toString(36).substr(2, 9),
                    text: `🔄 _Encaminhando para: ${queueName}_`,
                    sender: 'bot',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                };
                setMessages(prev => [...prev, newMsg]);
                const nextConn = edges.find(e => e.source === nodeId);
                if (nextConn) {
                    setTimeout(() => processNode(nextConn.target), 800);
                }
                break;
            }

            case 'changeUser': {
                const userName = node.data?.userName || 'um novo atendente';
                const newMsg: Message = {
                    id: Math.random().toString(36).substr(2, 9),
                    text: `👤 _Transferindo para: ${userName}_`,
                    sender: 'bot',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                };
                setMessages(prev => [...prev, newMsg]);
                const nextConn = edges.find(e => e.source === nodeId);
                if (nextConn) {
                    setTimeout(() => processNode(nextConn.target), 800);
                }
                break;
            }
        }
    }, [nodes, edges, variables]);

    const replaceVariables = (text: string) => {
        let result = text;
        Object.keys(variables).forEach(key => {
            result = result.replace(new RegExp(`{{${key}}}`, 'g'), variables[key]);
        });
        return result;
    };

    const handleSendMessage = () => {
        if (!inputText.trim() || isTyping) return;

        const userMsg: Message = {
            id: Math.random().toString(36).substr(2, 9),
            text: inputText,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        setMessages(prev => [...prev, userMsg]);
        const userText = inputText.trim();
        setInputText('');

        if (!currentNodeId) {
            resetSimulator();
            return;
        }

        const currentNode = nodes.find(n => n.id === currentNodeId);
        if (!currentNode) return;

        if (currentNode.data.type === 'menu') {
            const emojis = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
            const trimmed = userText.trim();
            let choice = parseInt(trimmed, 10);
            if (Number.isNaN(choice)) {
                const idx = emojis.indexOf(trimmed);
                if (idx >= 0) choice = idx + 1;
                else if (trimmed === '🔟') choice = 10;
                else choice = NaN;
            }
            const conn = !Number.isNaN(choice)
                ? edges.find(e => e.source === currentNodeId && e.sourceHandle === `a${choice}`)
                : undefined;

            if (conn) {
                setTimeout(() => processNode(conn.target), 500);
            } else {
                setTimeout(() => {
                    const errorMsg: Message = {
                        id: Math.random().toString(36).substr(2, 9),
                        text: '❌ Opção inválida. Escolha uma das opções acima (emoji ou número).',
                        sender: 'bot',
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    };
                    setMessages(prev => [...prev, errorMsg]);
                }, 500);
            }
        } else if (currentNode.data.type === 'question') {
            const key = currentNode.data.key;
            if (key) {
                setVariables(prev => ({ ...prev, [key]: userText }));
            }
            const conn = edges.find(e => e.source === currentNodeId);
            if (conn) {
                setTimeout(() => processNode(conn.target), 500);
            }
        } else if (currentNode.data.type === 'consultarFaturas' || currentNode.data.type === 'consultarFaturasV2') {
            const data = currentNode.data || {};
            const isV2 = currentNode.data.type === 'consultarFaturasV2';
            const parseChoice = (text: string) => {
                const emojis = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
                const trimmed = text.trim();
                const n = parseInt(trimmed, 10);
                if (!Number.isNaN(n)) return n;
                const idx = emojis.indexOf(trimmed);
                if (idx >= 0) return idx + 1;
                if (trimmed === '🔟') return 10;
                return NaN;
            };
            const choice = parseChoice(userText);

            if (faturasSimState.phase === 'askCpf') {
                const cpfOk = userText.replace(/\D/g, '').length === 11 || userText.replace(/\D/g, '').length === 14;
                if (!cpfOk) {
                    sendBotMessage(replaceVariables(data.cpfNotFoundMessage || 'CPF/CNPJ nao encontrado.'), 500);
                    return;
                }
                sendBotMessage(replaceVariables(
                    (data.confirmIdentityMessage || 'Encontramos o cadastro: *{{nome_cliente}}*\n1️⃣ - Sim\n2️⃣ - Nao').replace('{{nome_cliente}}', 'Joao Silva')
                ), 500);
                setFaturasSimState({ phase: 'confirmIdentity' });
            } else if (faturasSimState.phase === 'confirmIdentity') {
                if (choice === 1) {
                    sendBotMessage(replaceVariables(data.askLinkConsentMessage || 'Podemos usar este CPF nas proximas vezes?\n1️⃣ - Sim\n2️⃣ - Nao'), 500);
                    setFaturasSimState({ phase: 'askLinkConsent', clienteId: 1 });
                } else if (choice === 2) {
                    sendBotMessage(replaceVariables(data.identityDeniedMessage || '1️⃣ - Informar novamente\n2️⃣ - Aguardar atendimento'), 500);
                    setFaturasSimState({ phase: 'identityDenied' });
                }
            } else if (faturasSimState.phase === 'identityDenied') {
                if (choice === 1) {
                    sendBotMessage(replaceVariables(data.askCpfMessage || 'Informe seu CPF ou CNPJ:'), 500);
                    setFaturasSimState({ phase: 'askCpf', cpfAttempts: 0 });
                } else if (choice === 2) {
                    const branch = edges.find(e => e.source === currentNodeId && e.sourceHandle === 'noClient');
                    if (branch) setTimeout(() => processNode(branch.target), 500);
                }
            } else if (faturasSimState.phase === 'askLinkConsent') {
                if (choice === 1) setVariables(prev => ({ ...prev, cliente_id: 1 }));
                if (isV2) {
                    resolveContratoOrListFaturasV2Sim(currentNodeId, data, 1);
                } else {
                    showFaturasOrSendDirect(currentNodeId, data, data.quantidade || 3, 1);
                }
            } else if (faturasSimState.phase === 'chooseContract' && isV2) {
                const options = faturasSimState.contratoOptions || [];
                const selected = options[choice - 1];
                if (!selected) {
                    sendBotMessage(replaceVariables(data.invalidContractChoiceMessage || 'Opcao invalida. Informe o numero da lista ou o ID do contrato.'), 500);
                    return;
                }
                showFaturasOrSendDirect(currentNodeId, data, data.quantidade || 3, faturasSimState.clienteId || 1, selected.contratoId);
            } else if (faturasSimState.phase === 'choose') {
                const filtered = MOCK_FATURAS.filter(f =>
                    faturasSimState.selectedContratoId === undefined || f.contrato_id === faturasSimState.selectedContratoId
                );
                const idx = choice - 1;
                const fatura = filtered[idx];
                if (!fatura) {
                    sendBotMessage(replaceVariables(data.invalidChoiceMessage || 'Opcao invalida.'), 500);
                    return;
                }
                if (data.sendMode === 'link') {
                    sendBotMessage(`[Template enviar_fatura] Fatura #${fatura.id} - R$ ${fatura.valor}`, 500);
                } else {
                    sendBotMessage(`Segue dados da sua fatura\nFatura: #${fatura.id}\nValor: R$ ${fatura.valor}\nVencimento: ${fatura.data_vencimento}`, 500);
                    setTimeout(() => sendBotMessage('Linha digitavel: 12345...\nPIX: pix...', 500), 1200);
                    setTimeout(() => sendBotMessage(data.mensagem4 || 'Enviei os dados da fatura para pagamento.', 500), 2400);
                }
                const branch = edges.find(e => e.source === currentNodeId && e.sourceHandle === 'success');
                if (branch) setTimeout(() => processNode(branch.target), 3500);
            }
        } else {
            const conn = edges.find(e => e.source === currentNodeId);
            if (conn) {
                setTimeout(() => processNode(conn.target), 500);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header style WhatsApp */}
            <Box sx={{ bgcolor: '#075e54', p: 1.5, display: 'flex', alignItems: 'center', color: 'white' }}>
                <IconButton size="small" sx={{ color: 'white', mr: 1 }} onClick={onClose}>
                    <ArrowBackIcon />
                </IconButton>
                <Avatar sx={{ width: 40, height: 40, mr: 1.5 }}>F</Avatar>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        Flow Simulator
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        online
                    </Typography>
                </Box>
                <IconButton size="small" sx={{ color: 'white' }} onClick={resetSimulator} title="Reiniciar Simulação">
                    <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton size="small" sx={{ color: 'white' }}>
                    <MoreVertIcon />
                </IconButton>
            </Box>

            {/* Chat Body */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    p: 2,
                    bgcolor: '#e5ddd5',
                    backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                    backgroundRepeat: 'repeat',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}
            >
                {messages.map((msg) => (
                    <Box
                        key={msg.id}
                        sx={{
                            alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                            maxWidth: '85%',
                            position: 'relative'
                        }}
                    >
                        <Paper
                            sx={{
                                p: '6px 10px 8px 10px',
                                bgcolor: msg.sender === 'user' ? '#dcf8c6' : 'white',
                                borderRadius: 1.5,
                                borderTopRightRadius: msg.sender === 'user' ? 0 : 1.5,
                                borderTopLeftRadius: msg.sender === 'bot' ? 0 : 1.5,
                                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                minWidth: 60
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#303030' }}>
                                {renderFormattedText(msg.text)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.2, ml: 4 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(0,0,0,0.45)', mr: 0.5 }}>
                                    {msg.timestamp}
                                </Typography>
                                {msg.sender === 'user' && (
                                    <DoneAllIcon sx={{ fontSize: 13, color: '#4fc3f7' }} />
                                )}
                            </Box>
                        </Paper>
                    </Box>
                ))}
                {isTyping && (
                    <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%', position: 'relative' }}>
                        <Paper
                            sx={{
                                p: '6px 10px 8px 10px',
                                bgcolor: 'white',
                                borderRadius: 1.5,
                                borderTopLeftRadius: 0,
                                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                            }}
                        >
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'rgba(0,0,0,0.5)' }}>
                                digitando...
                            </Typography>
                        </Paper>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input WhatsApp style */}
            <Box sx={{ p: '10px', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Paper
                    sx={{
                        flexGrow: 1,
                        borderRadius: 5,
                        px: 2,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <TextField
                        fullWidth
                        variant="standard"
                        placeholder="Digite uma mensagem"
                        InputProps={{ disableUnderline: true }}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSendMessage();
                        }}
                    />
                </Paper>
                <IconButton
                    onClick={handleSendMessage}
                    sx={{
                        bgcolor: '#075e54',
                        color: 'white',
                        '&:hover': { bgcolor: '#128c7e' },
                        width: 45,
                        height: 45
                    }}
                >
                    <SendIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
        </Dialog>
    );
};
